import {Component, EventEmitter, Inject, OnDestroy, OnInit, Output} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {DeleteDialogModel} from './model/delete-dialog.model';
import {TtsControlEnum} from '@core/enums/tts-control.enum';
import {TtsHelperService} from '@shared/utils/tts-helper.service';
import {ReadingSpeedEnum} from '@core/enums/reading-speed.enum';
import {getLanguageEnumByLanguageCode} from '@core/enums/language.enum';
import {ReadingContentTypeEnum} from '@core/enums/reading-content-type.enum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TtsRestService} from '@core/modules/rest/text-to-speech/tts-rest.service';

@UntilDestroy()
@Component({
    selector: 'app-delete-confirmation-dialog',
    templateUrl: './delete-confirmation-dialog.component.html',
    styleUrls: ['./delete-confirmation-dialog.component.scss']
})
export class DeleteConfirmationDialogComponent implements OnInit, OnDestroy {
    @Output() closeDialogEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() deleteCommentEvent: EventEmitter<void> = new EventEmitter<void>();
    dialogTitle: string | undefined;
    dialogDescription: string | undefined;

    audio = new Audio();
    firstPlay = true;
    ttsUrl: string | undefined;
    repeatButtonActive = false;
    ttsIconState = TtsControlEnum.playState;

    constructor(private translate: TranslateService, @Inject(MAT_DIALOG_DATA) data: DeleteDialogModel,
                private ttsHelperService: TtsHelperService, private ttsService: TtsRestService) {
        this.dialogTitle = data.title;
        this.dialogDescription = data.description;
    }

    ngOnInit(): void {
        this.audio.addEventListener('ended', this.setButtonOnEnd, false);
        if (sessionStorage.getItem('audioMode')) {
            this.readText();
        }
    }

    onClose(): void {
        this.pauseAudio();
        this.closeDialogEvent.emit();
    }

    readText(): void {
        if (this.firstPlay) {
            this.ttsIconState = TtsControlEnum.pauseState;
            this.convertTextToSpeech();
        } else {
            if (this.audio.ended) {
                this.playAudio();
            } else {
                if (this.ttsIconState === TtsControlEnum.playState) {
                    this.resumeAudio();
                } else {
                    this.pauseAudio();
                }
            }
        }
    }

    convertTextToSpeech(): void {
        const removedHtmlTagsTitle = this.ttsHelperService.removeHTMLTags(this.dialogTitle);
        const removedHtmlTagsInfo = this.ttsHelperService.removeSSML(this.dialogDescription);
        const finalText = removedHtmlTagsTitle.concat('? ').concat(removedHtmlTagsInfo);

        const pollyObject = {
            text: finalText,
            readingSpeed: ReadingSpeedEnum.MEDIUM,
            readingLanguage: getLanguageEnumByLanguageCode(localStorage.getItem('languageCode')),
            readingContentType: ReadingContentTypeEnum.TEXT
        };

        this.firstPlay = false;
        this.repeatButtonActive = true;
        this.ttsService.getTTS(pollyObject).pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.ttsUrl = data;
            if (this.ttsUrl) {
                this.audio.src = this.ttsUrl;
                this.playAudio();
            }
        });
    }

    playAudio(): void {
        this.audio.load();
        this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
    }

    pauseAudio(): void {
        this.ttsIconState = TtsControlEnum.playState;
        if (!this.audio.ended) {
            this.audio.pause();
        }
    }

    resumeAudio(): void {
        if (!this.audio.ended) {
            if (this.ttsIconState === TtsControlEnum.pauseState) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            } else {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
        }
    }

    onDelete(): void {
        this.pauseAudio();
        this.deleteCommentEvent.emit();
    }

    setButtonOnEnd = (evt: { type: string; }) => {
        if (evt.type === 'ended') {
            this.ttsIconState = TtsControlEnum.playState;
        }
    }

    ngOnDestroy(): void {
        this.pauseAudio();
    }
}
